import * as Dialog from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import type { PropsWithChildren, ReactNode } from 'react';
import { useSpinDelay } from 'spin-delay';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../../components/Icon/Icon';
import { Spinner } from '../../components/Spinner/Spinner';
import { useIsMobile } from '../../hooks/useIsMobile';
import { button } from '../Button/base';

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type ModalProps = Dialog.DialogProps;

/**
 * @example
 * <Modal.Root open={open} onOpenChange={setOpen}>
    <Modal.Trigger>
      <Button>Edit profile</Button>
    </Modal.Trigger>
    <Modal.Content title="Your profile">
      <Modal.Body>
        { ... }
      </Modal.Body>
      <Modal.Actions>
        <Button intent="secondary" onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={() => setOpen(false)}>Confirm</Button>
      </Modal.Actions>
    </Modal.Content>
  </Modal.Root>
 */
const Modal = ({ open, onOpenChange, children }: ModalProps) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} aria-hidden={true}>
      {children}
    </Dialog.Root>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type ModalTriggerProps = Dialog.DialogTriggerProps & { ariaLabel?: string };

const ModalTrigger = ({ children, ariaLabel }: ModalTriggerProps) => {
  return (
    <Dialog.Trigger asChild aria-label={ariaLabel ?? 'Open Modal'}>
      {children}
    </Dialog.Trigger>
  );
};

const LoadingOverlay = () => {
  return (
    <div className="fixed inset-0 items-center flex justify-center bg-overlay/40 text-primary">
      <Spinner />
    </div>
  );
};

const ModalCloseButton = ({ ariaLabel }: { ariaLabel?: string }) => {
  return (
    <Dialog.Close
      aria-label={ariaLabel ?? 'Close Modal'}
      className={button({ intent: 'ghost', size: 'small' })}
    >
      <Icon name="close" size="large" />
    </Dialog.Close>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const modalContent = cva(
  'flex flex-col bg-surface rounded-m outline-none my-auto fixed inset-0 h-fit max-h-max box-border',
  {
    variants: {
      screenSize: {
        mobile: 'mx-l',
        desktop: 'w-[640px] mx-auto',
      },
    },
  },
);

type ModalContentProps = Dialog.DialogContentProps &
  Omit<VariantProps<typeof modalContent>, 'screenSize'> & {
    title?: ReactNode;
    loading?: boolean;
    ariaLabel?: string;
    closeAriaLabel?: string;
    showXinDialog?: boolean;
    ariaDescribedBy?: string;
    hideHeader?: boolean;
  };

const ModalContent = ({
  title,
  ariaLabel,
  closeAriaLabel,
  children,
  ariaDescribedBy,
  loading = false,
  showXinDialog = true,
  hideHeader = false,
}: ModalContentProps) => {
  const showLoadingIndicator = useSpinDelay(loading, {
    delay: 300,
    minDuration: 250,
    ssr: false,
  });
  const { isMobile } = useIsMobile();
  const resolvedCloseAriaLabel = closeAriaLabel ?? 'Close Modal';

  const accessibleName =
    ariaLabel ?? (typeof title === 'string' ? title : 'Modal Title');

  return (
    <Dialog.Portal>
      <Dialog.Overlay
        aria-label="Modal Overlay"
        className="fixed inset-0 bg-overlay/40"
      />
      <Dialog.Content
        aria-modal
        aria-describedby={ariaDescribedBy ?? undefined}
        className={modalContent({
          screenSize: isMobile ? 'mobile' : 'desktop',
        })}
        onInteractOutside={(event) => event.preventDefault()}
      >
        {showLoadingIndicator && <LoadingOverlay />}
        {hideHeader ? (
          <>
            <Dialog.Title className="sr-only">{accessibleName}</Dialog.Title>
            {showXinDialog && (
              <div className="absolute right-m top-m">
                <ModalCloseButton ariaLabel={resolvedCloseAriaLabel} />
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between p-m border-b">
            <Dialog.Title
              className="text-heading-s"
              aria-label={accessibleName}
            >
              {title}
            </Dialog.Title>
            {showXinDialog && (
              <ModalCloseButton ariaLabel={resolvedCloseAriaLabel} />
            )}
          </div>
        )}
        <div className="overflow-hidden flex flex-col">{children}</div>
      </Dialog.Content>
    </Dialog.Portal>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Body
 * -----------------------------------------------------------------------------------------------*/

const modalBody = cva('p-m max-h-[80vh] overflow-y-auto');

const ModalBody = ({ children }: PropsWithChildren<unknown>) => {
  return <div className={modalBody()}>{children}</div>;
};

/* -------------------------------------------------------------------------------------------------
 * Actions
 * -----------------------------------------------------------------------------------------------*/
const modalActions = cva('border-t p-m box-border flex justify-end');

type ModalActionsProps = PropsWithChildren<{
  ariaLabel?: string;
  // Actions default to half-width, sized for a two-button confirm/cancel
  // row. Pass this for a single full-width action instead.
  fullWidth?: boolean;
}>;

const ModalActions = ({ children, fullWidth = false }: ModalActionsProps) => {
  const { isMobile } = useIsMobile();

  return (
    <div className={modalActions()}>
      <div
        className={twMerge(
          'flex gap-x-m',
          isMobile || fullWidth ? 'w-full' : 'w-1/2',
        )}
      >
        {children}
      </div>
    </div>
  );
};

export {
  ModalActions as Actions,
  ModalBody as Body,
  ModalContent as Content,
  Modal as Root,
  ModalTrigger as Trigger,
  type ModalProps,
};
